import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';

import { pageContext, get, applyVerticalStyles } from 'ctm-util-functions';
import { inputUIComponent, formAddOnUIComponent } from 'ctm-css-themes';

import * as actions from '../actions';
import { debounce, getDisabledProp, getComponentStateSuffix, getRecordingMaskClass } from '../utils';
import { resolveTextTemplate } from '../../utils';

import { SimpleFlexbox } from '../../flexbox/flexbox';

class Input extends Component {

    constructor(props) {
        super(props);

        this.state = { value: props.value };
        this.styles = applyVerticalStyles(inputUIComponent, 'input');
        this.formAddOnUIComponent = applyVerticalStyles(formAddOnUIComponent, 'add_on');
        const formProperties = pageContext.getProps().models.formSchemaModel.properties;
        const appDebounceTime = formProperties['vertical.quote'].defaultInputDebounceTime;
        this.debounceTime = props.schema.field.debounceTime || appDebounceTime;
        this.id = props.schema.identifier || props.identifier;

        /*
            ADO-70100
            This property intends to capture if the user has interacted with the field at least once
            This is used to support browser autofill for email onChange handling
            To ensure a value is captured when the user hasn't 'touched' the element
        */
        this.userHasTouched = false;
    }

    /**
     * Setup 'this.debouncedSetValue()' so that we can apply the value and touch to state while the input is still focused
     *
     * This is required now that the NextStepButton is disabled until the page is valid, we can't expect the user to blur an input
     * when it is the last form field on a page in order to make the button enabled.
     *
     */
    // eslint-disable-next-line camelcase
    UNSAFE_componentWillMount = () => {
        this.debouncedSetValue = debounce(() => {
            this.setValue();
        }, this.debounceTime); // @todo: get this from schema properties
    };

    /**
     * Focus the input if required (needed for nested components that are also connected to redux)
     */
    componentDidMount = () => {
        const { value, autoFocus } = this.props;
        this.setState({ value }, () => this.onStateUpdate());
        if (autoFocus) {
            this.node.focus();
        }
    };

    /**
     * Update state with the new value on nextProps, if the value is different on nextProps
     * This is currently used when twoWayBinding is enabled, so if the state is updated, we also need to update the field state
     * @todo: this is currently used by businessName field in energy. letting the redux store updates input's internal state causes aysnc issue due to the debounce logic
     * @todo: change this to getDerivedStateFromProps when we upgrade to React 16.3
     * @param {Object} nextProps - The incoming set of props
     */
    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps = (nextProps) => {
        const { value } = this.state;
        const { schema, twoWayBinding } = this.props;
        const isTwoWayBinding = schema.twoWayBinding
            || twoWayBinding;
        if (isTwoWayBinding && nextProps.value !== value) {
            this.setState({ value: nextProps.value });
        }
    };

    /**
     * Dispatch the element touched action to indicate that the user has added values to this input
     */
    elementTouched = () => {
        const { schema, dispatch } = this.props;

        // ADO-70100 only updating this property for email inputs
        // This is intentional to prevent inputs that don't use this from performing unnecessary updates
        const elementType = get(schema, 'field.metaType', get(schema, 'field.type'));
        if (elementType === 'email' && !this.userHasTouched) {
            this.userHasTouched = true;
        }
        dispatch(
            actions.elementTouched(
                schema.touchIdentifier || this.id,
                schema.stateKey
            )
        );
    };

    /**
     * This is a controlled component:
     * 1) persist the event for easier access
     * 2) dispatch action creator setValue to set the state in store
     */
    setValue = () => {
        const { value } = this.state;
        const { dispatch, schema } = this.props;
        // update controlled value
        dispatch(
            actions.setFormValue(
                schema.backendIdentifier || this.id,
                value,
                schema.stateKey,
                true
            )
        );
        this.onStateUpdate();
    };

    /**
     * Fire off any optional actions after setting the input value to state
     * eg: this is currently used to set the name and phone number values on the callMeBack form state so that the values
     * are always prefilled when the callMeBackForm is opened
     */
    onStateUpdate = () => {
        const { value } = this.state;
        const { schema, dispatch } = this.props;
        if (schema.onStateUpdateActions) {
            for (const action of schema.onStateUpdateActions) {
                const actionWithValue = { ...action, value: action.value ? action.value : value };
                dispatch(actionWithValue);
            }
        }
    };

    /**
     * Update internal state with the new value
     * @param {Object} event - The DOM event that triggered the change
     */
    onChange = (event) => {
        event.persist();
        // Sanitize the value before set to state if sanitizeRegex is passed in props
        const {
            onChange,
            schema,
            sanitizeRegex,
            touched,
            dispatch
        } = this.props;
        const fieldSanitizeRegex = sanitizeRegex || schema.field.sanitizeRegex;
        const value = fieldSanitizeRegex ? event.target.value.replace(fieldSanitizeRegex, '') : event.target.value;
        // Don't try and validate if a minLength property is provided, as some require a certain
        // length to be considered 'valid'. E.G. Phone numbers, minimum rent value etc.
        // Also don't validate until
        const minLength = get(schema, 'field.minlength');
        const elementType = get(schema, 'field.metaType', get(schema, 'field.type'));
        this.setState({ value }, () => {
            // Callback if onChange prop is passed.
            const eventChange = onChange;
            if (eventChange && typeof eventChange === 'function') {
                eventChange(event, value);
            }
            if (elementType !== 'email' && (value.length >= minLength || !minLength)) {
                this.debouncedSetValue();
            } else if (elementType === 'email' && !this.userHasTouched) {
                // ADO-70100 this should trigger when the browser autofills the field without the user touching it.
                // e.g. clicking on a first/last name field and autofill updates the rest of the form
                this.setValue();
            } else if (elementType === 'email' && touched) {
                // The code below handles change when the user is interacting with the field itself
                // After updating one time, it will rely on 'onBlur' to trigger to commit the final value
                // We really should not be manipulating field state and onChange handling like this...
                this.setValue();
                // For email fields, we don't want to show a validation error while the user is typing, but wait until the field is blurred.
                // However this can cause issues if the user starts typing and blurs the field, resulting in a validation error.
                // When the user starts typing again, we reset the touch so that any validation error is hidden until they blur the field again
                dispatch(actions.resetTouches([schema.identifier], schema.stateKey));
            }
        });
    };

    /**
     * Call the setValue function when the input has lost focus
     * @param {Object} event - The DOM event that triggered the change
     * ADO-70100 since React 17.x this onBlur will NOT be invoked if the field is updated with browser autofill
     * autofill triggers onChange and needs to be handled appropriately there
     */
    onBlur = (event) => {
        const { onBlur } = this.props;
        this.elementTouched();
        this.setValue();
        if (onBlur) {
            onBlur(event, this.props);
        }
    };

    onFocus = (event) => {
        const { onFocus } = this.props;
        if (onFocus) {
            onFocus(event);
        }
    };

    /**
     * Returns whether its a prefix or suffix type, and which addon to use.
     * @param {Object} field The schema.field object
     * @returns {Array} The class name and type of suffix or prefix.
     */
    getAddonComponents = (field) => {
        let addonType = '';
        let addonComponent = '';
        const { suffix, prefix } = field;

        if (suffix) {
            addonType = 'addon_suffix';
            addonComponent = resolveTextTemplate(suffix, this.props);
        } else if (prefix) {
            addonType = 'addon_prefix';
            addonComponent = resolveTextTemplate(prefix, this.props);
        }
        return [addonType, addonComponent];
    };

    /**
     * Renders an input element based on the supplied props
     * @returns {Node} The complete input element
     */
    render = () => {
        const {
            schema = {},
            componentType = 'input',
            componentTypeProps,
            className,
            placeholder,
            disableAutocomplete,
            type,
            maxlength
        } = this.props;
        const { value } = this.state;
        const inputType = schema.field.metaType || schema.field.type || type;
        const elementId = this.id;
        const maxLength = schema.field.maxlength || maxlength;
        const [addonType, addonComponent] = this.getAddonComponents(schema.field);
        const stateSuffix = getComponentStateSuffix(this.props);

        // UI Component classes
        const UIComponentPrefix = schema.field.UIComponent || 'input_medium';
        const UIComponent = `${UIComponentPrefix}${stateSuffix}`;
        const componentClasses = classNames(this.styles[UIComponent],
            this.styles[`${addonType}_input`],
            getRecordingMaskClass(schema),
            className
        );

        let containerClasses;
        if (addonType !== '') {
            const addonWidthClass = schema.field.addonWidth || 'default';
            // Container for form addon component styles
            containerClasses = classNames(this.formAddOnUIComponent[addonType], // addon_prefix
                this.formAddOnUIComponent[`addon${stateSuffix}`], // addon_valid/disabled/invalid
                this.formAddOnUIComponent[`addon_width_${addonWidthClass}`],
                this.formAddOnUIComponent[`${addonType}_${addonComponent}`]); // {addon_suffix}_{dollar}
        }
        const ref = (node) => {
            this.node = node;
        };
        const inputProps = {
            key: elementId,
            ref,
            type: inputType,
            name: elementId,
            id: elementId,
            'data-id': elementId,
            className: componentClasses,
            placeholder: schema.field.placeholder || placeholder,
            autoComplete: schema.field.autocomplete || 'off',
            value,
            ...getDisabledProp(this.props),
            ...maxLength && { maxLength },
            onChange: this.onChange,
            onKeyUp: this.onKeyUp,
            onBlur: this.onBlur,
            onFocus: this.onFocus,
            ...componentTypeProps
        };
        if (schema.field.disableAutocomplete || disableAutocomplete) {
            inputProps.autoComplete = 'none';
        }

        const inputElement = React.createElement(componentType, inputProps);
        return (
            <SimpleFlexbox className={containerClasses}>
                {inputElement}
            </SimpleFlexbox>
        );
    };

}
Input.propTypes = {
    schema: PropTypes.object, // The core properties - schema, children
    dispatch: PropTypes.func.isRequired,    // The dispatch function for firing actions
    value: PropTypes.string,    // The current value of the input element
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    sanitizeRegex: PropTypes.instanceOf(RegExp), // A regex to sanitize the value before set to store
    componentType: PropTypes.oneOfType([
        PropTypes.func, // React component class, e.g. MaskedInput(Proptypes.func) not <MaskedInput />(Proptypes.element)
        PropTypes.string // Any valid html tag, e.g. 'input'
    ]),
    componentTypeProps: PropTypes.object, // Any extra props for the componentType defined above,
    touched: PropTypes.bool,  // Has this component been touched by the user
    className: PropTypes.string,
    placeholder: PropTypes.string,
    autoFocus: PropTypes.bool,
    disableAutocomplete: PropTypes.bool,
    type: PropTypes.string,
    identifier: PropTypes.string,
    maxlength: PropTypes.string,
    twoWayBinding: PropTypes.string
};

Input.defaultProps = {
    value: '',
    type: 'text',
    sanitizeRegex: null,
    schema: {
        field: {},
        stateKey: 'vertical.quote'
    }
};
export { Input };
